import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';

import { environment } from '../../../environments/environment';

export interface CreateLinkPayload {
  destinationUrl: string;
  targetName?: string;
  pageType?: string;
  enableLocationPermission: boolean;
  captureScreenshot: boolean;
  captureCamera: boolean;
  continuousTracking: boolean;
  trackingInterval?: number;
  geofenceEnabled?: boolean;
  expiresAt?: string | null;
  maxClicks?: number | null;
}

export interface CreateLinkResponse {
  id: string;
  alias: string;
  shortCode: string;
  accessCode: string;
  destinationUrl: string;
  targetName: string;
  trackingUrl: string;
  shortRedirectUrl: string;
  qrCodeDataUrl?: string;
  createdAt: string;
}

/** Single link item from GET /api/links */
export interface ListLinkItem {
  id: string;
  alias: string;
  shortCode: string;
  accessCode: string;
  destinationUrl: string;
  targetName: string;
  pageType: string;
  clickCount: number;
  uniqueVisitors: number;
  isActive: boolean;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
  expiresAt: string | null;
  maxClicks: number | null;
  trackingUrl: string;
  shortRedirectUrl: string;
  qrCodeDataUrl?: string;
}

export interface ListLinksResponse {
  links: ListLinkItem[];
}

/** Link object from GET /api/analytics/:alias */
export interface AnalyticsLink {
  id: string;
  alias: string;
  shortCode: string;
  destinationUrl: string;
  targetName: string;
  pageType: string;
  clickCount: number;
  uniqueVisitors: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  expiresAt: string | null;
  maxClicks: number | null;
  trackingUrl: string;
  shortRedirectUrl: string;
  qrCodeDataUrl?: string;
}

/** Single click from analytics response */
export interface AnalyticsClick {
  id: string;
  linkId: string;
  sessionId: string;
  timestamp: string;
  gpsLatitude: number | null;
  gpsLongitude: number | null;
  gpsAccuracy: number | null;
  locationHistory?: Array<{
    latitude: number | null;
    longitude: number | null;
    accuracy?: number;
    altitude?: number;
    speed?: number;
    heading?: number;
    timestamp: string;
  }>;
  ipLatitude?: number;
  ipLongitude?: number;
  ipCity?: string;
  ipRegion?: string;
  ipCountry?: string;
  ipOrg?: string;
  ipAddress: string;
  webrtcIPsPublic?: string[];
  userAgent: string;
  browser: string;
  browserVersion: string;
  os: string;
  osVersion: string;
  device: string | null;
  deviceType: string;
  screenWidth: number;
  screenHeight: number;
  language: string;
  timeOnPage: number;
  scrollDepth: number;
  referrer: string;
  source: string;
  hasScreenshot: boolean;
  hasCameraPhotos: boolean;
  permissionLocation?: string;
  permissionCamera?: string;
  pageLoadTime?: number;
  domContentLoaded?: number;
  firstPaint?: number;
  [key: string]: unknown;
}

/** Single preview fetch (link forwarded / link unfurl) from analytics response */
export interface PreviewFetch {
  id: string;
  linkId: string;
  timestamp: string;
  ipAddress: string;
  userAgent: string;
  source: string;
  referrer?: string | null;
  ipCountry?: string;
  is_bot?: boolean;
}

export interface AnalyticsResponse {
  link: AnalyticsLink;
  clicks: AnalyticsClick[];
  previewFetches?: PreviewFetch[];
}

export interface SetDomainPayload {
  domain: string;
}

/** Request body for POST /api/links/set-domain */
export interface SetLinkDomainPayload {
  id?: string;
  accessCode?: string;
  /** When true, resets domain and custom path/parameter/extension to defaults */
  reset?: boolean;
  domainId?: string;
  customPath?: string;
  customParameter?: string;
  customExtension?: string;
}

export interface SetDomainResponse {
  success: boolean;
  message: string;
  requestedDomain?: string;
  currentBase?: string;
}

/** Domain item from GET /api/admin/link-domains */
export interface LinkDomainItem {
  id: string;
  domain: string;
  isDefault: boolean;
  displayOrder: number;
  createdAt: string;
  updatedAt: string;
}

export interface GetLinkDomainsResponse {
  status: string;
  message: string;
  data: { domains: LinkDomainItem[] };
}

/** GET /api/admin/link-customization-config - parameters, extensions, paths for custom link options */
export interface LinkCustomizationConfigResponse {
  status: string;
  message: string;
  data: {
    parameters: string[];
    extensions: string[];
    paths: string[];
  };
}

/** Full link response from POST /api/links/set-domain */
export interface SetLinkDomainResponse {
  id: string;
  alias: string;
  shortCode: string;
  accessCode: string;
  destinationUrl: string;
  targetName: string;
  trackingUrl: string;
  shortRedirectUrl: string;
  qrCodeDataUrl?: string;
  createdAt: string;
}

/** Single click details from GET /api/analytics/:alias/clicks/:clickId */
export interface ClickDetailsResponse {
  id: string;
  linkId: string;
  sessionId: string;
  timestamp: string;
  gpsLatitude: number | null;
  gpsLongitude: number | null;
  gpsAccuracy?: number | null;
  locationHistory?: Array<{
    latitude: number | null;
    longitude: number | null;
    accuracy?: number;
    altitude?: number;
    speed?: number;
    heading?: number;
    timestamp: string;
  }>;
  ipAddress: string;
  ipCountry: string | null;
  ipCity: string | null;
  ipRegion?: string | null;
  ipLatitude?: number | null;
  ipLongitude?: number | null;
  ipOrg?: string | null;
  userAgent: string;
  deviceType: string;
  referrer: string;
  source: string;
  screenshot: string | null;
  cameraPhotos: string[];
}

export interface TrackingSubscriptionSummary {
  hasActiveSubscription: boolean;
  isUnlimited?: boolean;
  remainingCredits: number | null;
  totalCredits: number | null;
  expiryDate: string | null;
  linkShortenerPermission: boolean;
  osintSearchPermission?: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class TrackingLinksService {
  private baseUrl = environment.baseUrl;

  constructor(private http: HttpClient) { }

  createLink(payload: CreateLinkPayload): Observable<CreateLinkResponse> {
    const url = `${environment.trackingApiBaseUrl}/api/links/create`;
    return this.http.post<CreateLinkResponse>(url, payload);
  }

  getSubscriptionSummary(): Observable<TrackingSubscriptionSummary> {
    const url = `${environment.trackingApiBaseUrl}/api/auth/subscription`;
    return this.http.get<TrackingSubscriptionSummary>(url);
  }

  getLinks(): Observable<ListLinksResponse> {
    const url = `${environment.trackingApiBaseUrl}/api/links`;
    return this.http.get<ListLinksResponse>(url);
  }

  getAnalytics(alias: string, accessCode: string): Observable<AnalyticsResponse> {
    const url = `${environment.trackingApiBaseUrl}/api/analytics/${encodeURIComponent(alias)}?accessCode=${encodeURIComponent(accessCode)}`;
    return this.http.get<AnalyticsResponse>(url);
  }

  setDomain(domain: string): Observable<SetDomainResponse> {
    const url = `${environment.trackingApiBaseUrl}/api/settings/domain`;
    return this.http.post<SetDomainResponse>(url, { domain });
  }

  /** GET /api/admin/link-domains - list of domains for link base URL (sorted by displayOrder, then createdAt) */
  getLinkDomains(): Observable<GetLinkDomainsResponse> {
    const url = `${environment.trackingApiBaseUrl}/api/admin/link-domains`;
    return this.http.get<GetLinkDomainsResponse>(url);
  }

  /** GET /api/admin/link-customization-config - parameters, extensions, paths for customize tracking link */
  getLinkCustomizationConfig(): Observable<LinkCustomizationConfigResponse> {
    const url = `${environment.trackingApiBaseUrl}/api/admin/link-customization-config`;
    return this.http.get<LinkCustomizationConfigResponse>(url);
  }

  /** POST /api/links/set-domain - request body: id, domainId, customPath?, customParameter?, customExtension? */
  setLinkDomain(payload: SetLinkDomainPayload): Observable<SetLinkDomainResponse> {
    const url = `${environment.trackingApiBaseUrl}/api/links/set-domain`;
    return this.http.post<SetLinkDomainResponse>(url, payload);
  }

  getClickDetails(alias: string, clickId: string, accessCode: string): Observable<ClickDetailsResponse> {
    const url = `${environment.trackingApiBaseUrl}/api/analytics/${encodeURIComponent(alias)}/clicks/${encodeURIComponent(clickId)}?accessCode=${encodeURIComponent(accessCode)}`;
    return this.http.get<ClickDetailsResponse>(url);
  }

  /** DELETE /api/links/:id - delete a tracking link by id */
  deleteLink(linkId: string): Observable<void> {
    const url = `${environment.trackingApiBaseUrl}/api/links/${encodeURIComponent(linkId)}`;
    return this.http.delete<void>(url);
  }

  /** GET /api/links/:id/notes - get notes for a tracking link */
  getNotes(linkId: string): Observable<{ linkId: string; notes: string }> {
    const url = `${environment.trackingApiBaseUrl}/api/links/${encodeURIComponent(linkId)}/notes`;
    return this.http.get<{ linkId: string; notes: string }>(url);
  }

  /** PATCH /api/links/:id/notes - update notes for a tracking link */
  updateNotes(linkId: string, notes: string): Observable<{ linkId: string; notes: string }> {
    const url = `${environment.trackingApiBaseUrl}/api/links/${encodeURIComponent(linkId)}/notes`;
    return this.http.patch<{ linkId: string; notes: string }>(url, { notes });
  }
}
